import React, { useState } from "react";
import { api, formatNok } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, Consent } from "../site/formPrimitives";
import { CheckCircle2, Loader2, Send, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const empty = { quantity: 1, name: "", phone: "", email: "", message: "", consent: false, company: "" };

export default function PurchaseForm({ product }) {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const available = product.availability === "Tilgjengelig" && product.stock > 0;
  const maxQty = Math.min(product.stock || 1, 10);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Fyll inn navn";
    if (form.phone.trim().length < 5) e.phone = "Fyll inn telefonnummer";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Ugyldig e-postadresse";
    if (!form.consent) e.consent = "Du må samtykke for å sende";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (sending || !validate()) return;
    setSending(true);
    try {
      await api.post("/shop/purchase-requests", { ...form, quantity: Number(form.quantity), product_id: product.id });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende forespørselen. Prøv igjen.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-hairline bg-white p-6 text-center shadow-sm" data-testid="purchase-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-electric" />
        <h3 className="mt-3 font-heading text-xl font-bold text-navy">Takk!</h3>
        <p className="mt-2 text-sm text-ink/70">Vi har mottatt kjøpsforespørselen din og tar kontakt så snart som mulig.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-hairline bg-white p-5 shadow-sm sm:p-6" data-testid="purchase-form" noValidate>
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-navy"><ShoppingBag className="h-5 w-5 text-electric" /> Send kjøpsforespørsel</h3>
      <p className="mt-1 text-sm text-ink/60">Send en kjøpsforespørsel, så tar vi kontakt for å bekrefte tilgjengelighet og avtale kjøpet.</p>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-offwhite px-4 py-3 text-sm" data-testid="purchase-product-summary">
        <div className="min-w-0">
          <p className="font-semibold text-navy truncate">{product.name}</p>
          <p className="text-xs text-ink/50">Produkt-ID: {product.id.slice(0, 8)}</p>
        </div>
        <span className="font-heading text-base font-bold text-electric" data-testid="purchase-product-price">{formatNok(product.price)}</span>
      </div>

      {!available ? (
        <p className="mt-4 rounded-lg bg-slate-100 p-3.5 text-sm text-ink/70" data-testid="purchase-unavailable">
          Dette produktet er {product.availability === "Tilgjengelig" ? "utsolgt" : product.availability.toLowerCase()} og tar ikke imot nye kjøpsforespørsler.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" />
          <Field label="Navn" required error={errors.name}>
            <TextInput value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ditt navn" error={errors.name} data-testid="purchase-name" />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Telefonnummer" required error={errors.phone}>
              <TextInput type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+47 ..." error={errors.phone} data-testid="purchase-phone" />
            </Field>
            <Field label="E-post" required error={errors.email}>
              <TextInput type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="deg@epost.no" error={errors.email} data-testid="purchase-email" />
            </Field>
          </div>
          {product.stock > 1 && (
            <Field label="Antall">
              <SelectInput value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="purchase-quantity">
                {Array.from({ length: maxQty }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </SelectInput>
            </Field>
          )}
          <Field label="Melding (valgfritt)">
            <TextArea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Spørsmål eller ønsker" data-testid="purchase-message" />
          </Field>
          <div>
            <Consent checked={form.consent} onChange={(v) => set("consent", v)} testid="purchase-consent" />
            {errors.consent && <span className="mt-1 block text-xs font-medium text-red-600">{errors.consent}</span>}
          </div>
          <button type="submit" disabled={sending} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-warm px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-warm-dark disabled:opacity-60" data-testid="purchase-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send kjøpsforespørsel
          </button>
          <p className="text-xs text-ink/50">Dette er ikke en betaling eller bekreftet bestilling.</p>
        </div>
      )}
    </form>
  );
}
